import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import originicjaggery from "../../public/1.jpeg";
import naturaljaggery from "../../public/naturaljaggery.jpeg";
import jaggerypowder from "../../public/powder.jpg";
import jaggerycubes from "../../public/originicjaggery.jpeg";

type ProductKey =
  | "natural-jaggery"
  | "organic-jaggery"
  | "powder"
  | "cube";

type ProductDetail = {
  title: string;
  image: string;
  description: string;
  variants?: string[];
};

const PRODUCT_DETAILS: Record<ProductKey, ProductDetail> = {
  "natural-jaggery": {
    title: "Natural Jaggery",
        image: originicjaggery,

    description:
      "Natural jaggery is an unrefined traditional sweetener prepared by boiling fresh sugarcane juice and solidifying it into blocks, powder, or liquid form. It contains natural minerals like iron, calcium, and magnesium.",
    variants: ["250 gm", "500 gm", "1 kg", "5 kg", "10 kg"],

  },
  "organic-jaggery": {
    title: "Organic Jaggery",
    image: naturaljaggery,
    description:
      "Organic jaggery is an unrefined natural sweetener produced from organically grown sugarcane juice. The juice is clarified using natural plant-based clarification (like bhindi/okra or natural herbs) and concentrated by traditional boiling methods without chemicals or sulphur.",
    variants: ["250 gm", "500 gm", "1 kg", "5 kg", "10 kg"],
  },
  "powder": {
    title: "Jaggery Powder",
      image: jaggerypowder,
    description:
      "Jaggery Powder is a traditional, unrefined natural sweetener made from pure sugarcane juice. It is produced by concentrating sugarcane juice without using any chemicals or additives, preserving essential minerals and nutrients. Jaggery powder is widely used as a healthy alternative to refined sugar in food, beverages, and confectionery products.\n\nOur jaggery powder is carefully processed and hygienically packed to maintain its natural taste, aroma, and nutritional value, making it suitable for domestic consumption as well as international export markets.",
    variants: ["500 gm" , "1 kg","2 kg"],
  },
  "cube": {
    title: "Jaggery Cubes",
    image: jaggerycubes,
    description:
      "Jaggery Cubes are sweetener cubes made from concentrated sugarcane juice. These cubes are produced through a traditional process without the use of chemicals or artificial additives, preserving the natural nutrients and rich flavor of sugarcane.\n\nSmall jaggery cubes are convenient for direct consumption and are widely used as a healthier alternative to refined sugar. They are popular in tea, coffee, snacks, and traditional sweets.",
    variants: ["12 gms", "20 gms", "50 gms", "100 gm cubes"],
  },
};

function isProductKey(value: string | undefined): value is ProductKey {
  return (
    value === "natural-jaggery" ||
    value === "organic-jaggery" ||
    value === "powder" ||
    value === "cube"
  );
}

export default function ProductDetails() {
  const { productId } = useParams();

  const detail = useMemo(() => {
    if (!isProductKey(productId)) return null;
    return PRODUCT_DETAILS[productId];
  }, [productId]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, [productId]);

  const [imgSrc, setImgSrc] = useState<string | null>(null);
  useEffect(() => {
    setImgSrc(detail?.image ?? null);
  }, [detail]);

  if (!detail) {
    return (
      <section className="min-h-[70vh] py-16 md:py-24 bg-[#F8F9FA]">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
              Product not found
            </h1>
            <p className="text-[#2D3748] mb-8">
              The product you’re looking for doesn’t exist.
            </p>
            <Link 
              to="/#products"
              className="inline-flex items-center font-medium text-[#1A365D] hover:text-[#4A90E2] transition-colors"
            >
              <i className="ri-arrow-left-line mr-2"></i> Back to Products
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 md:py-24 bg-[#F8F9FA]">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-5xl mx-auto">
          <Link
            to="/#products"
            className="inline-flex items-center font-medium text-[#1A365D] hover:text-[#4A90E2] transition-colors mb-6"
          >
            <i className="ri-arrow-left-line mr-2"></i> Back to Products
          </Link>

          <div className="bg-white rounded-3xl overflow-hidden shadow-sm border border-black/5">
            <div className="relative w-full h-[220px] sm:h-[320px] md:h-[420px]">
              {imgSrc ? (
                <img
                  src={imgSrc}
                  alt={detail.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  onError={() => setImgSrc(null)}
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[#E2E8F0] via-[#CBD5E0] to-[#A0AEC0]" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent"></div>
              <h1 className="absolute bottom-6 left-6 right-6 text-3xl md:text-5xl font-bold text-white">
                {detail.title}
              </h1>
            </div>

            <div className="p-6 md:p-10">
              <div className="whitespace-pre-line text-[#2D3748] text-base md:text-lg leading-relaxed">
                {detail.description}
              </div>

              {detail.variants && detail.variants.length > 0 ? (
                <div className="mt-8">
                  <h2 className="text-xl md:text-2xl font-bold text-[#1A365D] mb-3">
                    Available pack sizes
                  </h2>
                  <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {detail.variants.map((v) => (
                      <li
                        key={v}
                        className="rounded-2xl border border-black/10 bg-[#F8F9FA] px-4 py-3 text-[#2D3748] font-medium text-sm md:text-base"
                      >
                        {v}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
